import type { PendingInvitationResponse } from '@stw/types';
import { ref } from 'vue';

import {
  acceptFolderShareInvitation,
  acceptGroupInvitation,
  acceptItemShareInvitation,
  declineFolderShareInvitation,
  declineGroupInvitation,
  declineItemShareInvitation,
  getPendingInvitations,
} from '@/api/invitation';
import { notifyDashboardChanged } from '@/composables/useDashboardRefresh';
import { useStwToast } from '@/utils/stwToast';

/**
 * Shared pending-invitation state (list, in-flight accept/decline tracking)
 * used by both the header bell dropdown and the full Settings history list —
 * same accept/decline flow, just rendered differently.
 */
export function usePendingInvitations() {
  const toast = useStwToast();
  const invitations = ref<PendingInvitationResponse[]>([]);
  const processingIds = ref(new Set<string>());

  const loadInvitations = async () => {
    try {
      invitations.value = await getPendingInvitations();
    } catch (error) {
      console.error('Failed to load invitations:', error);
    }
  };

  const removeInvitation = (id: string) => {
    invitations.value = invitations.value.filter((inv) => inv.id !== id);
    processingIds.value.delete(id);
  };

  const handleAccept = async (invitation: PendingInvitationResponse) => {
    processingIds.value.add(invitation.id);

    try {
      if (invitation.type === 'item_share') {
        const result = await acceptItemShareInvitation(invitation.token);
        removeInvitation(invitation.id);
        notifyDashboardChanged();
        toast.add({
          severity: 'success',
          summary: 'Model Added!',
          detail: `"${result.itemName}" has been added to your dashboard.`,
          life: 5000,
        });
      } else if (invitation.type === 'folder_share') {
        const result = await acceptFolderShareInvitation(invitation.token);
        removeInvitation(invitation.id);
        notifyDashboardChanged();
        toast.add({
          severity: 'success',
          summary: 'Folder Accepted!',
          detail:
            result.itemCount > 0
              ? `${result.itemCount} model${result.itemCount === 1 ? '' : 's'} added to the folder "${result.folderName}" in your dashboard.`
              : 'None of the models in this folder can be shared any more (the sender deleted them or can no longer share them).',
          life: 5000,
        });
      } else {
        const result = await acceptGroupInvitation(invitation.token);
        removeInvitation(invitation.id);
        toast.add({
          severity: 'success',
          summary: 'Joined Group!',
          detail: `You are now a member of "${result.groupName}".`,
          life: 5000,
        });
      }
    } catch (error: any) {
      processingIds.value.delete(invitation.id);
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to accept invitation',
        life: 5000,
      });
    }
  };

  const handleDecline = async (invitation: PendingInvitationResponse) => {
    processingIds.value.add(invitation.id);

    try {
      if (invitation.type === 'item_share') {
        await declineItemShareInvitation(invitation.token);
      } else if (invitation.type === 'folder_share') {
        await declineFolderShareInvitation(invitation.token);
      } else {
        await declineGroupInvitation(invitation.token);
      }
      removeInvitation(invitation.id);
      toast.add({
        severity: 'info',
        summary: 'Declined',
        detail: 'Invitation declined.',
        life: 3000,
      });
    } catch (error: any) {
      processingIds.value.delete(invitation.id);
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to decline invitation',
        life: 5000,
      });
    }
  };

  return {
    invitations,
    processingIds,
    loadInvitations,
    handleAccept,
    handleDecline,
  };
}
